import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PageBodyTitle,
  Card,
  Table,
  ListPageFilterBar,
  PageLoader,
  StatusBadge,
} from "../../../design-system";
import { theme } from "../../../design-system/theme";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  getQRRequestList,
  createQRRequest,
} from "../../../../utils/api";

import CsvDownApi from "../../../../components/ExportFiles/CsvDownApi";

//
// 🔹 FILTERS
//
const STATUS_OPTIONS = [
  { label: "COMPLETED", value: "COMPLETED" },
  { label: "INIT", value: "INIT" },
];

const FILTER_INITIAL = [
  { name: "handle", type: "text", value: "", placeholder: "Search by Handle" },
  {
    name: "status",
    type: "select",
    value: "",
    placeholder: "Select Status",
    options: STATUS_OPTIONS,
  },
  { name: "fromDate", type: "date", value: "" },
  { name: "toDate", type: "date", value: "" },
];

//
// 🔹 STYLES
//
const PageWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

//
// 🔹 ROW MENU
//
const RowMenu = ({ row, onView, onDownload }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          disabled={row.status === "INIT"}
          onClick={() => {
            setAnchorEl(null);
            onView(row);
          }}
        >
          View
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onDownload(row);
          }}
        >
          Download
        </MenuItem>
      </Menu>
    </>
  );
};

//
// 🔥 MAIN COMPONENT
//
export default function QRRequestV2() {
  const navigate = useNavigate();

  const [controls, setControls] = useState([...FILTER_INITIAL]);
  const [appliedControls, setAppliedControls] = useState([...FILTER_INITIAL]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createCount, setCreateCount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ CSV FIX
  const csvWrapperRef = useRef(null);
  const [csvParams, setCsvParams] = useState({
    handle: "",
    pagination: { pageNo: 1, pageSize: 1000 },
    fromDate: "",
    toDate: "",
    status: "",
    orderId: "",
  });

  //
  // 🔹 PAYLOAD FIX (IMPORTANT)
  //
  const buildPayload = (controls, pageIndex, pageSize) => {
    const get = (n) => controls.find((c) => c.name === n)?.value ?? "";

    const statusVal = get("status");

    return {
      handle: get("handle") || "",
      status:
        typeof statusVal === "object"
          ? statusVal?.value
          : statusVal || "",
      fromDate: get("fromDate") || "",
      toDate: get("toDate") || "",
      pagination: { pageNo: pageIndex + 1, pageSize },
    };
  };

  //
  // 🔹 FETCH
  //
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getQRRequestList(
        buildPayload(appliedControls, pageIndex, pageSize)
      );

      const data = res?.data ?? res;

      if (data?.code === "0000") {
        const meta = data.metaInfo;
        const content = meta?.data || [];

        setRows(content);
        setTotalElements(Number(meta.totalElements) || 0);

        if (content.length === 0) {
          toast.info("No data found");
        }
      } else {
        toast.error(data.message);
        setRows([]);
      }
    } catch {
      toast.error("API error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedControls, pageIndex, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  //
  // 🔹 FILTERS
  //
  const handleFilterChange = (name, value) => {
    setControls((prev) =>
      prev.map((c) => (c.name === name ? { ...c, value } : c))
    );
  };

  const onSearch = () => {
    setAppliedControls(controls.map((c) => ({ ...c })));
    setPageIndex(0);
  };

  const onReset = () => {
    const reset = FILTER_INITIAL.map((f) => ({ ...f, value: "" }));
    setControls(reset);
    setAppliedControls(reset);
    setPageIndex(0);
  };

  //
  // 🔹 ACTIONS
  //
  const handleView = (row) => {
    navigate(`/inventory-management/QR-code-inventory?orderId=${row.id}`);
  };

  // ✅ DOWNLOAD FIX
  const handleDownload = async (row) => {
    setCsvParams({
      orderId: row.id,
      pagination: { pageNo: 1, pageSize: 1000 },
    });

    await new Promise((r) => setTimeout(r, 150));

    const wrapper = csvWrapperRef.current;
    const btn = wrapper?.querySelector("button, span, a");

    btn?.click();
  };

  //
  // 🔹 CREATE
  //
  const handleCreate = async () => {
    if (!createCount || Number(createCount) <= 0) {
      toast.error("Enter valid count");
      return;
    }

    setIsSubmitting(true);

    try {
      await createQRRequest({ capacity: Number(createCount) });

      toast.success("QR Request Created");
      setConfirmOpen(false);
      setCreateCount("");
      fetchList();
    } catch {
      toast.error("Create failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  //
  // 🔹 TABLE
  //
  const columns = [
    { key: "id", label: "ID" },
    { key: "handle", label: "Handle" },
    { key: "capacity", label: "Capacity" },
    {
      key: "status",
      label: "Status",
      render: (_, r) => (r?.status ? <StatusBadge status={r.status} /> : "—"),
    },
    { key: "createdBy", label: "Created By" },
    { key: "updatedBy", label: "Updated By" },
    {
      key: "createdAt",
      label: "Created At",
      render: (_, r) =>
        r.createdAt ? new Date(r.createdAt).toLocaleString() : "-",
    },
    {
      key: "updatedAt",
      label: "Updated At",
      render: (_, r) =>
        r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "-",
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <RowMenu row={row} onView={handleView} onDownload={handleDownload} />
      ),
    },
  ];

  if (loading && rows.length === 0) return <PageLoader />;

  return (
    <PageWrapper>
      <TopBar>
        <PageBodyTitle>Physical QR Generation</PageBodyTitle>

        <Button variant="contained" onClick={() => setConfirmOpen(true)}>
          New Request
        </Button>
      </TopBar>

      <Card>
        <ListPageFilterBar
          filters={controls}
          onFilterChange={handleFilterChange}
          onSearch={onSearch}
          onReset={onReset}
        />

        <Table
          data={rows}
          columns={columns}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: "No Data Found" }}
          pagination={{
            currentPage: pageIndex + 1,
            pageSize,
            total: totalElements,
            onPageChange: (p) => setPageIndex(p - 1),
            onPageSizeChange: (s) => {
              setPageSize(s);
              setPageIndex(0);
            },
          }}
        />
      </Card>

      {/* ✅ Hidden CSV */}
      <div ref={csvWrapperRef} style={{ display: "none" }}>
        <CsvDownApi
          url={"/payment/api/vpa/listing-inventory"}
          params={csvParams}
          method="post"
          reportName="QRRequests.csv"
          title="Download CSV"
        />
      </div>

      {/* CREATE MODAL */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}  maxWidth="sm" PaperProps={{
    sx: { width: "400px" }  // 🔥 controlled width
  }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          New QR Request
          <IconButton onClick={() => setConfirmOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Enter Count*" 
            type="number"
            fullWidth
            value={createCount}
            onChange={(e) => setCreateCount(e.target.value)}
             InputLabelProps={{ shrink: true }}
            sx={{
              mt: 1,
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              isSubmitting ||
              !createCount ||
              Number(createCount) <= 0
            }
          >
            {isSubmitting ? <CircularProgress size={18} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
}